import React, {useEffect} from 'react';
import {useForm} from 'react-hook-form';

import {useAppDispatch, useAppSelector} from './state/hooks';
import {updateBaseUrl} from './state/baseUrl';
import {useSafeNavigate as useNavigate} from './util/safeNavigate';
import {Input} from './components/ui/input';
import {Button} from './components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './components/ui/form';

interface FormData {
  baseUrl: string;
}

/**
 * First time app setup.
 */
export const Setup = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const baseUrlState = useAppSelector((state) => state.baseUrl);

  const form = useForm<FormData>({
    defaultValues: {
      baseUrl: baseUrlState.value,
    },
  });

  useEffect(() => {
    if (baseUrlState.validated) {
      navigate('/login');
    }
  }, [baseUrlState.validated]);

  const onSubmit = (data: FormData) => {
    console.log('onSubmit', data.baseUrl);
    dispatch(updateBaseUrl(data.baseUrl));
  };

  const getStatusIcon = () => {
    if (baseUrlState.validating) return '⏳';
    if (baseUrlState.error) return '❌';
    if (baseUrlState.validated) return '✅';
    return '⏸️';
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        <div>
          <h2 className="text-3xl font-bold mb-4 text-secondary">Setup</h2>
          <p className="text-muted-foreground mb-4">
            Welcome to Nudge! This app needs a small amount of configuration
            before you can use it.
          </p>
          <p className="text-muted-foreground mb-4">
            Please enter the URL of the Nudge API you wish to connect to. We
            will save this URL on your computer so that you don't need to enter
            it more than once.
          </p>
          <p className="text-muted-foreground mb-4">
            When in doubt, you can try the default value of{' '}
            <pre className="inline-block bg-muted p-1 rounded-md">/</pre>. (Just
            a single forward slash.)
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="baseUrl"
              render={({field}) => (
                <FormItem>
                  <FormLabel>Nudge API</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="nudge.mydepartment.org"
                        className="pr-8"
                        {...field}
                      />
                    </FormControl>
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm">
                      {getStatusIcon()}
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={baseUrlState.validating}
            >
              {baseUrlState.validating ? 'Validating...' : 'Save'}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};
